import RiQuillPenAiLine from '@ben/icons/quill-pen-ai-line';
import Button from '@ben/orion/button';
import { Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import Typography, { TypographyVariant } from '../../typography';

interface ManageBenefitFeaturesHeaderProps {
  onGenerateWithAi?: () => void;
  onAddManually: () => void;
  isLoadingGenerateWithAi?: boolean;
}

const ManageBenefitFeaturesHeader = ({
  onGenerateWithAi,
  isLoadingGenerateWithAi,
  onAddManually,
}: ManageBenefitFeaturesHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const canGenerateWithAi = !!onGenerateWithAi;

  return (
    <Wrapper>
      <Typography variant={TypographyVariant.h3}>
        {t('manageBenefitFeatures.title', { defaultValue: 'Features' })}
      </Typography>
      <Typography
        variant={TypographyVariant.bodyMd1}
        color={theme.palette.neutral[90]}
      >
        {t('manageBenefitFeatures.subtitle', {
          defaultValue:
            'Provide a detailed breakdown of the benefit to help employees better understand what is included.',
        })}
      </Typography>
      <ButtonGroup>
        {/* The Tooltip is active (not disabled) only when the Button is disabled (i.e., when canGenerateWithAi is false). */}
        <Tooltip
          label={t('manageBenefitFeatures.generateWithAiTooltip')}
          disabled={canGenerateWithAi}
        >
          <Button
            variant="outline"
            onClick={onGenerateWithAi}
            rightIcon={RiQuillPenAiLine}
            loading={isLoadingGenerateWithAi}
            disabled={!canGenerateWithAi}
          >
            {t('manageBenefitFeatures.generateWithAi', {
              defaultValue: 'Generate with AI',
            })}
          </Button>
        </Tooltip>
        <Button variant="outline" onClick={onAddManually}>
          {t('manageBenefitFeatures.addManually', {
            defaultValue: 'Add manually',
          })}
        </Button>
      </ButtonGroup>
    </Wrapper>
  );
};

export default ManageBenefitFeaturesHeader;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(3)};
  `}
`;

const ButtonGroup = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
    padding-top: ${theme.spacing(1.5)};
  `}
`;
